import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { useCallback, useContext, useMemo, useState } from 'react';
import { AuthContext } from '../../state/auth';

export interface ClientService {
  id: number;
  type?: string;
  name: string;
  clinical: boolean;
  ccgCommissioned: boolean;
  disabled: boolean;
  active: boolean;
  serviceIdentifier: string;
  serviceName: string;
  ccgName: string;
  status: string; // TODO: Narrow to valid status values
  hasBookings: boolean;
  defaultInternalReference: string;
}

const emptyList: number[] = [];

const useClientServices = (
  // A client id: number from the auth context, or the string/number carried by
  // a company chosen in the registration form.
  clientParam: number | string | undefined = undefined,
  status: string | undefined = undefined,
  onlyActive = true
) => {
  const [clientServices, setClientServices] = useState<ClientService[]>([]);

  const apiRequest = useApiRequest();

  const {
    currentClient,
    bookableServices: bookableServiceIds = emptyList,
    actions: { refreshAuthToken = () => {} } = {},
  } = useContext(AuthContext);

  const bookableServices = useMemo(
    () =>
      clientParam && clientParam !== currentClient
        ? []
        : clientServices.filter(({ id }) => bookableServiceIds.includes(id)),
    [clientParam, currentClient, clientServices, bookableServiceIds]
  );

  const client = clientParam || currentClient;

  const requestValid = !!client;

  const clearData = useCallback(async () => {
    setClientServices([]);
  }, []);

  const fetchData = useCallback(async () => {
    const {
      data: { clientServices: services = [] },
    } = (await apiRequest.get(
      `/v1/clients/client_services?client_id=${client}`,
      {
        params: { status, onlyActive },
      }
    )) as { data: { clientServices: ClientService[] } };

    setClientServices(services);
  }, [apiRequest, client, status, onlyActive]);

  const add = useCallback(
    // The new-service field groups collected by the registration form.
    async (services: object[]) => {
      if (!requestValid) throw new Error('Not logged in');

      const { data: clientServices } = (await apiRequest.post(
        `/v1/clients/client_services?client_id=${currentClient}`,
        {
          services,
        }
      )) as { data: ClientService[] };

      setClientServices(prev => [...prev, ...clientServices]);

      refreshAuthToken();

      return clientServices;
    },
    [apiRequest, currentClient, requestValid, refreshAuthToken]
  );

  const update = useCallback(
    async (clientService: ClientService) => {
      if (!requestValid) throw new Error('Not logged in');

      const { data: host } = await apiRequest.patch(
        `/v1/clients/client_services/${clientService.id}?client_id=${client}`,
        {
          clientService,
        }
      );

      await fetchData();

      return host;
    },
    [apiRequest, requestValid, fetchData, client]
  );

  const destroy = useCallback(
    async (clientService: Pick<ClientService, 'id'>) => {
      if (!requestValid) throw new Error('Not logged in');

      await apiRequest.delete(
        `/v1/clients/client_services/${clientService.id}?client_id=${client}`
      );

      await fetchData();
    },
    [apiRequest, requestValid, fetchData, client]
  );

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return {
    data: clientServices,
    bookableServices,
    loading,
    error,
    refresh,
    add,
    update,
    destroy,
  };
};

export default useClientServices;
